import {
  getSingletonHighlighter,
  type BundledLanguage,
  type Highlighter,
  type ThemedToken,
} from 'shiki';

const HIGHLIGHT_THEME = 'github-dark-default';

const PRELOADED_LANGUAGES: BundledLanguage[] = [
  'tsx',
  'typescript',
  'jsx',
  'javascript',
  'json',
  'bash',
  'css',
  'scss',
  'html',
  'markdown',
  'yaml',
];

const LANGUAGE_ALIASES: Record<string, BundledLanguage> = {
  bash: 'bash',
  cjs: 'javascript',
  css: 'css',
  html: 'html',
  javascript: 'javascript',
  js: 'javascript',
  json: 'json',
  jsx: 'jsx',
  markdown: 'markdown',
  md: 'markdown',
  mjs: 'javascript',
  sass: 'scss',
  scss: 'scss',
  sh: 'bash',
  shell: 'bash',
  ts: 'typescript',
  tsx: 'tsx',
  typescript: 'typescript',
  yaml: 'yaml',
  yml: 'yaml',
};

const FALLBACK_LANGUAGE: BundledLanguage = 'tsx';
const MAX_CACHE_ENTRIES = 200;

export interface HighlightedCodeResult {
  language: BundledLanguage;
  tokens: ThemedToken[][];
}

let highlighterPromise: Promise<Highlighter> | null = null;

const highlightCache = new Map<string, Promise<HighlightedCodeResult>>();

function pruneHighlightCache() {
  while (highlightCache.size > MAX_CACHE_ENTRIES) {
    const firstKey = highlightCache.keys().next().value;
    if (!firstKey) break;
    highlightCache.delete(firstKey);
  }
}

async function getHighlighter() {
  if (!highlighterPromise) {
    highlighterPromise = getSingletonHighlighter({
      themes: [HIGHLIGHT_THEME],
      langs: PRELOADED_LANGUAGES,
    });
  }

  return highlighterPromise;
}

export function resolveCodeLanguage(language?: string): BundledLanguage {
  if (!language) return FALLBACK_LANGUAGE;

  const normalizedLanguage = language.trim().toLowerCase();
  return LANGUAGE_ALIASES[normalizedLanguage] ?? FALLBACK_LANGUAGE;
}

export function normalizeCodeSnippet(code: string) {
  return code.replace(/\r\n?/g, '\n');
}

export async function highlightCode(
  code: string,
  language?: string,
): Promise<HighlightedCodeResult> {
  const normalizedCode = normalizeCodeSnippet(code);
  const resolvedLanguage = resolveCodeLanguage(language);
  const cacheKey = `${HIGHLIGHT_THEME}::${resolvedLanguage}::${normalizedCode}`;
  const cached = highlightCache.get(cacheKey);

  if (cached) {
    return cached;
  }

  const highlightPromise = (async () => {
    const highlighter = await getHighlighter();
    const result = highlighter.codeToTokens(normalizedCode, {
      lang: resolvedLanguage,
      theme: HIGHLIGHT_THEME,
      tokenizeMaxLineLength: 4000,
      tokenizeTimeLimit: 120,
    });

    return {
      language: resolvedLanguage,
      tokens: result.tokens,
    };
  })();

  highlightCache.set(cacheKey, highlightPromise);
  pruneHighlightCache();

  try {
    return await highlightPromise;
  } catch (error) {
    highlightCache.delete(cacheKey);
    throw error;
  }
}
